import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  HStack,
  Box,
  Text,
  Divider,
  useToast,
  Grid,
  GridItem,
  IconButton,
  Card,
  CardBody,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  useColorModeValue,
} from "@chakra-ui/react";
import { FiPlus, FiTrash2, FiStar } from "react-icons/fi";
import { digitalFilesService } from "../digitalFilesService";

const JobHistoryModal = ({ isOpen, onClose, employee, onSuccess }) => {
  // Dark mode colors
  const mutedColor = useColorModeValue("gray.600", "gray.400");
  const bgColor = useColorModeValue("white", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const [activeTab, setActiveTab] = useState(0);
  const [jobHistory, setJobHistory] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [formData, setFormData] = useState({
    positionInfo: {
      jobTitle: "",
      department: "",
      reportingTo: "",
      employmentType: "Full-time",
      isCurrentPosition: false,
      startDate: "",
      endDate: "",
    },
    compensation: {
      salary: "",
      currency: "USD",
      payFrequency: "Monthly",
      bonuses: [],
      benefits: [],
    },
    performanceReviews: [],
    promotions: [],
    trainingPrograms: [],
    disciplinaryActions: [],
  });

  const [loading, setLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (employee?.jobHistory) {
      setJobHistory(employee.jobHistory);
    }
  }, [employee]);

  const handleCreateNewJob = () => {
    setSelectedJob(null);
    setFormData({
      positionInfo: {
        jobTitle: "",
        department: "",
        reportingTo: "",
        employmentType: "Full-time",
        isCurrentPosition: false,
        startDate: "",
        endDate: "",
      },
      compensation: {
        salary: "",
        currency: "USD",
        payFrequency: "Monthly",
        bonuses: [],
        benefits: [],
      },
      performanceReviews: [],
      promotions: [],
      trainingPrograms: [],
      disciplinaryActions: [],
    });
    setActiveTab(1);
  };

  const handleEditJob = (job) => {
    setSelectedJob(job);
    setFormData(job);
    setActiveTab(1);
  };

  const handleSubmit = async () => {
    if (!employee) return;

    try {
      setLoading(true);
      if (selectedJob) {
        // Update existing job history
        await digitalFilesService.jobHistory.updateJobHistory(
          selectedJob._id,
          formData
        );
        toast({
          title: "Job history updated",
          status: "success",
          duration: 3000,
        });
      } else {
        // Create new job history
        await digitalFilesService.jobHistory.createJobHistory(
          employee.employeeId,
          formData
        );
        toast({
          title: "Job history added",
          status: "success",
          duration: 3000,
        });
      }

      onSuccess();
      setActiveTab(0);
      setSelectedJob(null);
    } catch (error) {
      toast({
        title: "Error saving job history",
        description: error.message,
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (path, value) => {
    setFormData((prev) => {
      const keys = path.split(".");
      const lastKey = keys.pop();
      const target = keys.reduce(
        (obj, key) => (obj[key] = obj[key] || {}),
        prev
      );
      target[lastKey] = value;
      return { ...prev };
    });
  };

  const addArrayItem = (arrayName, template = {}) => {
    setFormData((prev) => ({
      ...prev,
      [arrayName]: [...(prev[arrayName] || []), template],
    }));
  };

  const updateArrayItem = (arrayName, index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [arrayName]: prev[arrayName].map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const removeArrayItem = (arrayName, index) => {
    setFormData((prev) => ({
      ...prev,
      [arrayName]: prev[arrayName].filter((_, i) => i !== index),
    }));
  };

  const renderJobHistoryList = () => (
    <VStack spacing={4} align="stretch">
      <HStack justify="space-between">
        <Text fontSize="lg" fontWeight="bold">
          Job History for {employee?.name}
        </Text>
        <Button
          leftIcon={<FiPlus />}
          colorScheme="blue"
          onClick={handleCreateNewJob}
        >
          Add New Position
        </Button>
      </HStack>

      {jobHistory.length === 0 ? (
        <Text color={mutedColor} textAlign="center" py={8}>
          No job history records found. Add the first position to get started.
        </Text>
      ) : (
        jobHistory.map((job, index) => (
          <Card key={job._id || index}>
            <CardBody>
              <HStack justify="space-between" align="start">
                <VStack align="start" spacing={2} flex={1}>
                  <HStack>
                    <Text fontSize="lg" fontWeight="bold">
                      {job.positionInfo?.jobTitle}
                    </Text>
                    {job.positionInfo?.isCurrentPosition && (
                      <Badge colorScheme="green">Current Position</Badge>
                    )}
                  </HStack>
                  <Text color={mutedColor}>{job.positionInfo?.department}</Text>
                  <HStack spacing={4} fontSize="sm" color={mutedColor}>
                    <Text>
                      {new Date(
                        job.positionInfo?.startDate
                      ).toLocaleDateString()}{" "}
                      -
                      {job.positionInfo?.endDate
                        ? new Date(
                            job.positionInfo?.endDate
                          ).toLocaleDateString()
                        : "Present"}
                    </Text>
                    <Text>{job.positionInfo?.employmentType}</Text>
                  </HStack>
                  {job.compensation?.salary && (
                    <Text fontWeight="medium">
                      ${job.compensation.salary.toLocaleString()}{" "}
                      {job.compensation.currency} (
                      {job.compensation.payFrequency})
                    </Text>
                  )}
                </VStack>
                <VStack spacing={2}>
                  <Button size="sm" onClick={() => handleEditJob(job)}>
                    Edit
                  </Button>
                  <HStack spacing={1}>
                    {job.performanceReviews?.length > 0 && (
                      <Badge colorScheme="blue">
                        {job.performanceReviews.length} Reviews
                      </Badge>
                    )}
                    {job.promotions?.length > 0 && (
                      <Badge colorScheme="purple">
                        {job.promotions.length} Promotions
                      </Badge>
                    )}
                  </HStack>
                </VStack>
              </HStack>
            </CardBody>
          </Card>
        ))
      )}

      {/* Career Summary */}
      {jobHistory.length > 0 && (
        <Card bg="gray.50">
          <CardBody>
            <Text fontSize="lg" fontWeight="bold" mb={3}>
              Career Summary
            </Text>
            <StatGroup>
              <Stat>
                <StatLabel>Total Positions</StatLabel>
                <StatNumber>{jobHistory.length}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>Current Position</StatLabel>
                <StatNumber>
                  {jobHistory.find((j) => j.positionInfo?.isCurrentPosition)
                    ?.positionInfo?.jobTitle || "N/A"}
                </StatNumber>
              </Stat>
              <Stat>
                <StatLabel>Average Rating</StatLabel>
                <StatNumber>
                  {jobHistory.flatMap((j) => j.performanceReviews || [])
                    .length > 0
                    ? (
                        jobHistory
                          .flatMap(
                            (j) =>
                              j.performanceReviews?.map((r) => r.rating) || []
                          )
                          .reduce((sum, rating) => sum + rating, 0) /
                        jobHistory.flatMap((j) => j.performanceReviews || [])
                          .length
                      ).toFixed(1)
                    : "N/A"}
                </StatNumber>
              </Stat>
            </StatGroup>
          </CardBody>
        </Card>
      )}
    </VStack>
  );

  const renderJobForm = () => (
    <VStack spacing={6} align="stretch">
      <HStack justify="space-between">
        <Text fontSize="lg" fontWeight="bold">
          {selectedJob ? "Edit" : "Add New"} Job Position
        </Text>
        <Button variant="outline" onClick={() => setActiveTab(0)}>
          Back to List
        </Button>
      </HStack>

      <Tabs variant="enclosed" size="sm">
        <TabList>
          <Tab>Position Info</Tab>
          <Tab>Compensation</Tab>
          <Tab>Performance</Tab>
          <Tab>Training</Tab>
        </TabList>

        <TabPanels>
          {/* Position Information */}
          <TabPanel>
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <FormControl isRequired>
                <FormLabel>Job Title</FormLabel>
                <Input
                  value={formData.positionInfo.jobTitle}
                  onChange={(e) =>
                    updateFormData("positionInfo.jobTitle", e.target.value)
                  }
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Department</FormLabel>
                <Input
                  value={formData.positionInfo.department}
                  onChange={(e) =>
                    updateFormData("positionInfo.department", e.target.value)
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>Reporting To</FormLabel>
                <Input
                  value={formData.positionInfo.reportingTo}
                  onChange={(e) =>
                    updateFormData("positionInfo.reportingTo", e.target.value)
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>Employment Type</FormLabel>
                <Select
                  value={formData.positionInfo.employmentType}
                  onChange={(e) =>
                    updateFormData(
                      "positionInfo.employmentType",
                      e.target.value
                    )
                  }
                >
                  <option value="Full-time">Full-time</option>
                  <option value="Part-time">Part-time</option>
                  <option value="Contract">Contract</option>
                  <option value="Internship">Internship</option>
                  <option value="Freelance">Freelance</option>
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Start Date</FormLabel>
                <Input
                  type="date"
                  value={formData.positionInfo.startDate}
                  onChange={(e) =>
                    updateFormData("positionInfo.startDate", e.target.value)
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>End Date (leave empty if current)</FormLabel>
                <Input
                  type="date"
                  value={formData.positionInfo.endDate}
                  onChange={(e) =>
                    updateFormData("positionInfo.endDate", e.target.value)
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>Is Current Position?</FormLabel>
                <Select
                  value={formData.positionInfo.isCurrentPosition.toString()}
                  onChange={(e) =>
                    updateFormData(
                      "positionInfo.isCurrentPosition",
                      e.target.value === "true"
                    )
                  }
                >
                  <option value="false">No</option>
                  <option value="true">Yes</option>
                </Select>
              </FormControl>
            </Grid>
          </TabPanel>

          {/* Compensation */}
          <TabPanel>
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <FormControl>
                <FormLabel>Salary</FormLabel>
                <Input
                  type="number"
                  value={formData.compensation.salary}
                  onChange={(e) =>
                    updateFormData("compensation.salary", e.target.value)
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>Currency</FormLabel>
                <Select
                  value={formData.compensation.currency}
                  onChange={(e) =>
                    updateFormData("compensation.currency", e.target.value)
                  }
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="INR">INR</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Pay Frequency</FormLabel>
                <Select
                  value={formData.compensation.payFrequency}
                  onChange={(e) =>
                    updateFormData("compensation.payFrequency", e.target.value)
                  }
                >
                  <option value="Monthly">Monthly</option>
                  <option value="Weekly">Weekly</option>
                  <option value="Bi-weekly">Bi-weekly</option>
                  <option value="Annual">Annual</option>
                </Select>
              </FormControl>
            </Grid>

            {/* Benefits */}
            <Box mt={6}>
              <HStack justify="space-between" mb={3}>
                <Text fontWeight="semibold">Benefits</Text>
                <Button
                  size="sm"
                  leftIcon={<FiPlus />}
                  onClick={() =>
                    addArrayItem("compensation.benefits", {
                      name: "",
                      description: "",
                    })
                  }
                >
                  Add Benefit
                </Button>
              </HStack>
              <VStack spacing={2}>
                {formData.compensation.benefits.map((benefit, index) => (
                  <HStack key={index} w="full">
                    <Input
                      placeholder="Benefit name"
                      value={benefit.name}
                      onChange={(e) =>
                        updateArrayItem(
                          "compensation.benefits",
                          index,
                          "name",
                          e.target.value
                        )
                      }
                    />
                    <Input
                      placeholder="Description"
                      value={benefit.description}
                      onChange={(e) =>
                        updateArrayItem(
                          "compensation.benefits",
                          index,
                          "description",
                          e.target.value
                        )
                      }
                    />
                    <IconButton
                      icon={<FiTrash2 />}
                      colorScheme="red"
                      variant="ghost"
                      onClick={() =>
                        removeArrayItem("compensation.benefits", index)
                      }
                    />
                  </HStack>
                ))}
              </VStack>
            </Box>
          </TabPanel>

          {/* Performance Reviews */}
          <TabPanel>
            <HStack justify="space-between" mb={4}>
              <Text fontWeight="semibold">Performance Reviews</Text>
              <Button
                size="sm"
                leftIcon={<FiPlus />}
                onClick={() =>
                  addArrayItem("performanceReviews", {
                    reviewDate: "",
                    reviewer: "",
                    rating: "",
                    comments: "",
                    goals: "",
                    achievements: "",
                  })
                }
              >
                Add Review
              </Button>
            </HStack>
            <VStack spacing={4}>
              {formData.performanceReviews.map((review, index) => (
                <Card key={index} w="full">
                  <CardBody>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3} mb={3}>
                      <FormControl>
                        <FormLabel fontSize="sm">Review Date</FormLabel>
                        <Input
                          type="date"
                          size="sm"
                          value={review.reviewDate}
                          onChange={(e) =>
                            updateArrayItem(
                              "performanceReviews",
                              index,
                              "reviewDate",
                              e.target.value
                            )
                          }
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Reviewer</FormLabel>
                        <Input
                          size="sm"
                          value={review.reviewer}
                          onChange={(e) =>
                            updateArrayItem(
                              "performanceReviews",
                              index,
                              "reviewer",
                              e.target.value
                            )
                          }
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Rating (1-5)</FormLabel>
                        <HStack>
                          <Input
                            type="number"
                            min="1"
                            max="5"
                            size="sm"
                            value={review.rating}
                            onChange={(e) =>
                              updateArrayItem(
                                "performanceReviews",
                                index,
                                "rating",
                                e.target.value
                              )
                            }
                          />
                          <HStack spacing={1}>
                            {[...Array(5)].map((_, i) => (
                              <FiStar
                                key={i}
                                color={i < review.rating ? "gold" : "gray"}
                                size={16}
                              />
                            ))}
                          </HStack>
                        </HStack>
                      </FormControl>
                    </Grid>
                    <VStack spacing={3}>
                      <FormControl>
                        <FormLabel fontSize="sm">Comments</FormLabel>
                        <Textarea
                          size="sm"
                          value={review.comments}
                          onChange={(e) =>
                            updateArrayItem(
                              "performanceReviews",
                              index,
                              "comments",
                              e.target.value
                            )
                          }
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Goals</FormLabel>
                        <Textarea
                          size="sm"
                          value={review.goals}
                          onChange={(e) =>
                            updateArrayItem(
                              "performanceReviews",
                              index,
                              "goals",
                              e.target.value
                            )
                          }
                        />
                      </FormControl>
                    </VStack>
                    <IconButton
                      icon={<FiTrash2 />}
                      colorScheme="red"
                      variant="ghost"
                      size="sm"
                      mt={2}
                      onClick={() =>
                        removeArrayItem("performanceReviews", index)
                      }
                      alignSelf="flex-end"
                    />
                  </CardBody>
                </Card>
              ))}
            </VStack>
          </TabPanel>

          {/* Training Programs */}
          <TabPanel>
            <HStack justify="space-between" mb={4}>
              <Text fontWeight="semibold">Training Programs</Text>
              <Button
                size="sm"
                leftIcon={<FiPlus />}
                onClick={() =>
                  addArrayItem("trainingPrograms", {
                    programName: "",
                    provider: "",
                    startDate: "",
                    endDate: "",
                    status: "Completed",
                    certificate: "",
                    skills: "",
                  })
                }
              >
                Add Training
              </Button>
            </HStack>
            <VStack spacing={3}>
              {formData.trainingPrograms.map((training, index) => (
                <Card key={index} w="full">
                  <CardBody>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <FormControl>
                        <FormLabel fontSize="sm">Program Name</FormLabel>
                        <Input
                          size="sm"
                          value={training.programName}
                          onChange={(e) =>
                            updateArrayItem(
                              "trainingPrograms",
                              index,
                              "programName",
                              e.target.value
                            )
                          }
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Provider</FormLabel>
                        <Input
                          size="sm"
                          value={training.provider}
                          onChange={(e) =>
                            updateArrayItem(
                              "trainingPrograms",
                              index,
                              "provider",
                              e.target.value
                            )
                          }
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Start Date</FormLabel>
                        <Input
                          type="date"
                          size="sm"
                          value={training.startDate}
                          onChange={(e) =>
                            updateArrayItem(
                              "trainingPrograms",
                              index,
                              "startDate",
                              e.target.value
                            )
                          }
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">End Date</FormLabel>
                        <Input
                          type="date"
                          size="sm"
                          value={training.endDate}
                          onChange={(e) =>
                            updateArrayItem(
                              "trainingPrograms",
                              index,
                              "endDate",
                              e.target.value
                            )
                          }
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Status</FormLabel>
                        <Select
                          size="sm"
                          value={training.status}
                          onChange={(e) =>
                            updateArrayItem(
                              "trainingPrograms",
                              index,
                              "status",
                              e.target.value
                            )
                          }
                        >
                          <option value="Completed">Completed</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Planned">Planned</option>
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Certificate</FormLabel>
                        <Input
                          size="sm"
                          value={training.certificate}
                          onChange={(e) =>
                            updateArrayItem(
                              "trainingPrograms",
                              index,
                              "certificate",
                              e.target.value
                            )
                          }
                        />
                      </FormControl>
                    </Grid>
                    <FormControl mt={3}>
                      <FormLabel fontSize="sm">Skills Acquired</FormLabel>
                      <Textarea
                        size="sm"
                        value={training.skills}
                        onChange={(e) =>
                          updateArrayItem(
                            "trainingPrograms",
                            index,
                            "skills",
                            e.target.value
                          )
                        }
                      />
                    </FormControl>
                    <IconButton
                      icon={<FiTrash2 />}
                      colorScheme="red"
                      variant="ghost"
                      size="sm"
                      mt={2}
                      onClick={() => removeArrayItem("trainingPrograms", index)}
                      alignSelf="flex-end"
                    />
                  </CardBody>
                </Card>
              ))}
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </VStack>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: "full", md: "6xl" }}
      scrollBehavior="inside"
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent bg={bgColor}>
        <ModalHeader px={{ base: 4, md: 6 }}>
          Job History Management
          {employee && (
            <Text fontSize="sm" color={mutedColor}>
              for {employee.name}
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody px={{ base: 4, md: 6 }}>
          <Tabs index={activeTab} onChange={setActiveTab}>
            <TabList
              overflowX="auto"
              css={{
                "&::-webkit-scrollbar": { display: "none" },
                scrollbarWidth: "none",
              }}
            >
              <Tab minW={{ base: "auto", md: "unset" }}>Job History</Tab>
              <Tab minW={{ base: "auto", md: "unset" }}>Position Details</Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={{ base: 0, md: 4 }}>
                {renderJobHistoryList()}
              </TabPanel>
              <TabPanel px={{ base: 0, md: 4 }}>{renderJobForm()}</TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
        <ModalFooter
          px={{ base: 4, md: 6 }}
          flexDirection={{ base: "column", md: "row" }}
          gap={2}
        >
          {activeTab === 1 && (
            <>
              <Button
                variant="ghost"
                onClick={() => setActiveTab(0)}
                width={{ base: "100%", md: "auto" }}
                minH="44px"
              >
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleSubmit}
                isLoading={loading}
              >
                {selectedJob ? "Update" : "Save"} Job History
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default JobHistoryModal;
